import type {
  Group,
  GroupMetaEntry,
  HumanizeDurationOptions,
  RerunResponse,
  State,
  TimelineEvent
} from './types'

import { format, parseISO } from 'date-fns'

import { groupMetaKeyNameMapper, reportFileTypeMapper } from './constants'
import { API_URL } from '../../../API'

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
})

export function formatGroupMeta(meta: Group): GroupMetaEntry[] {
  const keys: Array<keyof Group> = [
    'number',
    'name',
    'release_type',
    'release_day',
    'zelis_eligible',
    'checks_only',
    'activation_date'
  ]

  return keys
    .map(key => {
      const raw = meta[key]

      if (raw == null) {
        return {
          key,
          value: raw
        }
      }

      return {
        key,
        label: groupMetaKeyNameMapper[key],
        value:
          key === 'activation_date' && typeof raw === 'string'
            ? format(parseISO(raw), 'MM/dd/yyyy')
            : key === 'release_day' && typeof raw === 'number'
              ? ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][
                  raw - 1
                ]
              : raw.toString(10)
      }
    })
    .filter(entry => entry.value != null)
}

export function syncFiltersToUrlParams(
  filters: State['filters'],
  searchQuery: string,
  pagination: State['pagination']
): URLSearchParams {
  const searchParams = new URLSearchParams()

  // Add status IDs
  if (filters.statusIds.length > 0) {
    searchParams.set('statusIds', filters.statusIds.join(','))
  }

  // Add location IDs
  if (filters.locationIds.length > 0) {
    searchParams.set('locationIds', filters.locationIds.join(','))
  }

  // Add date filters
  if (filters.date.from) {
    searchParams.set('dateFrom', filters.date.from)
  }
  if (filters.date.to) {
    searchParams.set('dateTo', filters.date.to)
  }

  // Add scheduled release date filters
  if (filters.scheduledReleaseDate.from) {
    searchParams.set('scheduledDateFrom', filters.scheduledReleaseDate.from)
  }
  if (filters.scheduledReleaseDate.to) {
    searchParams.set('scheduledDateTo', filters.scheduledReleaseDate.to)
  }

  // Add flags
  if (filters.flags.length > 0) {
    searchParams.set('flags', filters.flags.join(','))
  }

  // Add release type
  if (filters.releaseType.length > 0) {
    searchParams.set('releaseType', filters.releaseType.join(','))
  }

  // Add exact group filters
  if (filters.groupNumber) {
    searchParams.set('groupNumber', filters.groupNumber)
  }
  if (filters.groupName) {
    searchParams.set('groupName', filters.groupName)
  }

  if (Array.isArray(filters.amount) && filters.amount.length > 0) {
    filters.amount.forEach(amount => {
      searchParams.append('amount', amount)
    })
  }

  // Add search query
  if (searchQuery) {
    searchParams.set('search', searchQuery)
  }

  // Add pagination
  searchParams.set('page', String(pagination.currentPage))
  searchParams.set('pageSize', String(pagination.pageSize))

  return searchParams
}

export function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural
}

export function humanizeDuration(ms: number, options?: HumanizeDurationOptions): string {
  const short = options?.short ?? false

  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) {
    if (short) {
      return `${round(hours)}h`
    }

    return `${round(hours)} ${pluralize(round(hours), 'hour', 'hours')}`
  }

  if (minutes > 0) {
    if (short) {
      return `${round(minutes)}m`
    }

    return `${round(minutes)} ${pluralize(round(minutes), 'minute', 'minutes')}`
  }

  if (short) {
    return `${round(seconds)}s`
  }

  return `${round(seconds)} ${pluralize(round(seconds), 'second', 'seconds')}`
}

export function uniquePredicate<T>(value: T, index: number, array: T[]): boolean {
  return array.indexOf(value) === index
}

export function round<T>(value: T, precision = 2): number | T {
  if (typeof value !== 'number') {
    return value
  }
  const m = 10 ** precision
  return Math.round(value * m) / m
}

export function isRerunResponse(data: unknown): data is RerunResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'actions' in data &&
    Array.isArray(data.actions) &&
    data.actions.every(
      action =>
        typeof action === 'object' && action !== null && 'key' in action && 'success' in action
    )
  )
}

export function formatValue(value?: number | string | null): string | number | null | undefined {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return currencyFormatter.format(value)
  }

  return value
}

export const getFileName = (event: TimelineEvent): string | undefined => {
  if (event.type === 'cr-report') {
    return event.data.name
  }

  if (event.type === 'lcr-file') {
    return event.data.original_filename
  }

  if (event.type === 'scheduled-file') {
    return event.data.name
  }

  if (event.type === 'nomi835') {
    return event.data.name
  }

  if (event.type === 'nomi_status_file') {
    return event.data.name
  }

  if (event.type === 'sent-to-zelis') {
    return event.data.filename
  }

  if (event.type === 'received-from-zelis') {
    return event.data.filename
  }

  if (event.type === 'payment-file') {
    return event.data.filename
  }

  return undefined
}

export const getFileHref = (event: TimelineEvent, token: string | null): string | undefined => {
  if (event.type === 'cr-report') {
    return `${API_URL}/files/${encodeURIComponent(event.data.name)}?event-type-id=34&file-type-id=${reportFileTypeMapper[event.data.type as keyof typeof reportFileTypeMapper]}&token=${encodeURIComponent(token ?? '')}&report-id=${encodeURIComponent(event.data.id)}`
  }

  if (event.type === 'lcr-file') {
    return `${API_URL}/files/${encodeURIComponent(event.data.original_filename)}?event-type-id=1&file-type-id=6&token=${encodeURIComponent(token ?? '')}&report-id=${encodeURIComponent(event.data.id)}`
  }

  if (event.type === 'scheduled-file') {
    return `${API_URL}/files/${encodeURIComponent(event.data.name)}?event-type-id=200&file-type-id=200&token=${encodeURIComponent(token ?? '')}`
  }

  if (event.type === 'nomi835') {
    return `${API_URL}/files/from-path/${encodeURIComponent(event.data.path)}?event-type-id=200&file-type-id=200&token=${encodeURIComponent(token ?? '')}`
  }

  if (event.type === 'nomi_status_file') {
    return `${API_URL}/files/from-path/${encodeURIComponent(event.data.path)}?event-type-id=200&file-type-id=200&token=${encodeURIComponent(token ?? '')}`
  }

  if (event.type === 'sent-to-zelis') {
    return `${API_URL}/files/${encodeURIComponent(event.data.filename)}?event-type-id=2&file-type-id=2&token=${encodeURIComponent(token ?? '')}`
  }

  if (event.type === 'received-from-zelis') {
    return `${API_URL}/files/${encodeURIComponent(event.data.filename)}?event-type-id=3&file-type-id=3&token=${encodeURIComponent(token ?? '')}`
  }

  if (event.type === 'payment-file') {
    return `${API_URL}/files/${encodeURIComponent(event.data.filename)}?event-type-id=1&file-type-id=1&token=${encodeURIComponent(token ?? '')}`
  }

  if (event.type === 'payment-file-manual') {
    return `${API_URL}/files/${encodeURIComponent(event.data.name)}?event-type-id=1&file-type-id=1&token=${encodeURIComponent(token ?? '')}`
  }

  if (event.type === 'lcr-file-manual') {
    return `${API_URL}/files/${encodeURIComponent(event.data.name)}?event-type-id=1&file-type-id=6&token=${encodeURIComponent(token ?? '')}&report-id=${encodeURIComponent(event.data.id)}`
  }

  return undefined
}
